import { AUTH_EVENTS } from '@/lib/events/constants';
import { emitEvent } from '@/lib/events/helpers';

import { ERROR_CODE } from './error-code';
import {
  ForbiddenApiError,
  type ForbiddenApiErrorAccessibles,
} from './error.forbidden';
import { InternalServerApiError } from './error.internal-server';
import { NoOrganizationAccessError } from './error.no-organization-access';
import { PersonalEmailLoginDeniedError } from './error.personal-email-login-denied';
import { PersonalEmailNotAllowedError } from './error.personal-email-not-allowed';
import { ReauthRequiredError } from './error.reauth-required';
import { UnauthorizedApiError } from './error.unauthorized';
import { HTTP_STATUS } from './http-status';
import { isReauthRequiredCode } from './reauth';
import { resolveLocalizedDisplayMessage } from './resolve-display-message';

const APP_INTEGRATIONS_SETTINGS_REGEX = /^\/app-integrations\/[^/]+\/settings$/;

/** 本ハンドラが参照する認証エラー body のフィールド。 */
type AuthErrorBody = {
  code?: unknown;
  accessibles?: ForbiddenApiErrorAccessibles;
  displayMessages?: Record<string, string>;
  displayMessage?: string;
};

/** body を非破壊で JSON として読む。非 JSON / 消費済みは空オブジェクト。 */
async function parseErrorBody(response: Response): Promise<AuthErrorBody> {
  try {
    const json: unknown = await response.clone().json();
    return json !== null && typeof json === 'object'
      ? (json as AuthErrorBody)
      : {};
  } catch {
    return {};
  }
}

/** 外部認可サーバー / IdP リダイレクトページ（ここでの 401 はログアウトさせない）。 */
function isExternalAuthPath(): boolean {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  return (
    path.startsWith('/apps/marketplace') ||
    path.startsWith('/software/library') ||
    APP_INTEGRATIONS_SETTINGS_REGEX.test(path) ||
    path.startsWith('/oauth2/')
  );
}

/** 403: code / accessibles から該当エラーを throw する。 */
function handleForbidden(body: AuthErrorBody): never {
  if (body.code === ERROR_CODE.NO_ORGANIZATION_ACCESS) {
    throw new NoOrganizationAccessError();
  }
  if (body.code === ERROR_CODE.PERSONAL_EMAIL_NOT_ALLOWED) {
    throw new PersonalEmailNotAllowedError(
      resolveLocalizedDisplayMessage(body),
    );
  }
  if (body.code === ERROR_CODE.PERSONAL_EMAIL_LOGIN_DENIED) {
    throw new PersonalEmailLoginDeniedError(
      resolveLocalizedDisplayMessage(body),
    );
  }
  if (body.accessibles) {
    throw new ForbiddenApiError(body.accessibles);
  }
  throw new InternalServerApiError();
}

/**
 * 401: 再認証要求 (AAL2_REQUIRED / MFA_REAUTH_REQUIRED) は ReauthRequiredError を
 * throw（handleApiErrorWithReauth が捕捉して再認証→再実行）、それ以外はログアウトする。
 */
async function handleUnauthorized(response: Response): Promise<void> {
  if (isExternalAuthPath()) return;

  const body = await parseErrorBody(response);
  if (isReauthRequiredCode(body.code)) {
    throw new ReauthRequiredError(resolveLocalizedDisplayMessage(body));
  }

  emitEvent(AUTH_EVENTS.UNAUTHORIZED, { response });
  throw new UnauthorizedApiError();
}

/**
 * 401/403 レスポンスを解釈して該当エラーを throw する（不要な 401 は何もしない）。
 * openapi-fetch ミドルウェアと oRPC authFetch で共有する。
 */
export async function handleAuthErrorResponse(
  response: Response,
): Promise<void> {
  if (response.status === HTTP_STATUS.FORBIDDEN) {
    handleForbidden(await parseErrorBody(response));
  }
  if (response.status === HTTP_STATUS.UNAUTHORIZED) {
    await handleUnauthorized(response);
  }
}
